import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import inputBgMd from "@assets/images/backgrounds/inputBgMd.webp";
import useApiError from "@hooks/useApiError";
import useApiHook from "@hooks/useApiHook";
import modalBorder from "@assets/images/borders/modalBorder.webp";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";
import useNotification from "@src/hooks/useNotification";
import useOnclickOutside from "react-cool-onclickoutside";
import { getAddressInitialValue, isFieldEmpty } from "@src/utils/TernaryHelper";
import { getValueOrDefault } from "@src/utils/ConditionHelper";
import {
  countryNameToCode,
  parseAddressComponents,
  ADDRESS_VALIDATION_URL,
} from "@src/utils/AddressHelper";
import DeleteConfirmation from "../Downloads/DeleteConfirmation";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { IAddAddress, IAddress } from "@src/types/AppTypes";

const AddAddress = React.forwardRef<HTMLDivElement, IAddAddress>(
  (
    {
      closeModal,
      address,
      isEdit,
      title,
      updateAddresses,
      index,
      id: formId,
      addresses,
      deteteRef,
    },
    ref
  ) => {
    const validationRef = useRef<number>(0);
    const queryClient = useQueryClient();
    const {
      ready,
      value,
      suggestions: { status, data },
      setValue,
      clearSuggestions,
    } = usePlacesAutocomplete({
      debounce: 300,
    });
    const [country, setCountry] = useState<string>(() =>
      getAddressInitialValue(isEdit, address, "country")
    );
    const [secondaryStreet, setSecondaryStreet] = useState<string>(() =>
      getAddressInitialValue(isEdit, address, "street_name2")
    );
    const [state, setState] = useState<string>(() =>
      getAddressInitialValue(isEdit, address, "state")
    );
    const [city, setCity] = useState<string>(() =>
      getAddressInitialValue(isEdit, address, "city")
    );
    const [isPrimary, setIsPrimary] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const id = useId();
    const addAddressEndpoint = useApiHook({
      method: "post",
      url: "/addresses",
    });
    const updateApiAddressEndPoint = useApiHook({
      method: "put",
      url: "/addresses",
    });
    const setApiError = useApiError();
    const [transparentLoader, setTransparentLoader] = useState<boolean>(false);
    const [street_name, setStreet_name] = useState<string>(() =>
      getAddressInitialValue(isEdit, address, "street_name")
    );
    const [isPrimaryCheckboxDisable, setIsPrimaryCheckboxDisable] =
      useState<boolean>(false);

    const [zip_code, setZip_code] = useState<string>(() =>
      getAddressInitialValue(isEdit, address, "zip_code")
    );
    const [error, setError] = useState<string>("");
    const flashNotification = useNotification();
    const [isAddressValidating, setIsAddressValidating] =
      useState<boolean>(false);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [isCountryUnsupportedByGoogle, setIsCountryUnsupportedByGoogle] =
      useState<boolean>(false);
    const addressValidator = useCallback(
      ({
        street_name,
        city,
        state,
        zip_code,
        country,
      }: {
        street_name: string;
        city: string;
        state: string;
        zip_code: string;
        country: string;
      }) => {
        if (
          isFieldEmpty(state) ||
          isFieldEmpty(street_name) ||
          isFieldEmpty(zip_code) ||
          isFieldEmpty(country) ||
          isFieldEmpty(city)
        ) {
          setError("Please fill all the required fields");
          return false;
        }

        if (error) {
          setError("");
        }

        return true;
      },
      [error]
    );

    const submitAddress = useCallback(
      async ({
        city,
        street_name,
        state,
        country,
        zip_code,
        is_primary,
        street_name2,
        id,
        isEdit,
      }: {
        city: string;
        street_name: string;
        state: string;
        country: string;
        zip_code: string;
        is_primary: boolean;
        street_name2: string;
        id: string;
        isEdit: boolean;
      }) => {
        if (!isEdit) {
          return await addAddressEndpoint<{
            status: string;
            message: string;
            address: IAddress;
          }>({
            address: {
              city,
              street_name,
              state,
              country,
              zip_code,
              is_primary,
              street_name2,
            },
          });
        } else {
          return await updateApiAddressEndPoint<{
            status: string;
            message: string;
            address: IAddress;
          }>(
            {
              address: {
                city,
                street_name,
                state,
                country,
                zip_code,
                is_primary,
                street_name2,
              },
            },
            "/" + id
          );
        }
      },
      [addAddressEndpoint, updateApiAddressEndPoint]
    );

    const submit = useCallback(
      async (
        {
          street_name,
          city,
          state,
          zip_code,
          country,
          id,
          street_name2,
        }: {
          street_name: string;
          city: string;
          state: string;
          zip_code: string;
          country: string;
          id: string;
          street_name2: string;
        },
        is_primary: boolean
      ) => {
        if (!addressValidator({ city, country, state, street_name, zip_code }))
          return;

        try {
          setTransparentLoader(true);
          const res = await submitAddress({
            street_name,
            city,
            state,
            zip_code,
            country,
            id,
            street_name2,
            is_primary,
            isEdit,
          });
          if (res?.data.status) {
            flashNotification({ status: true, message: res.data.message });
            closeModal(false);

            // here remove address list query, not invalidate.
            queryClient.removeQueries({ queryKey: ["get-address-list"] });
            // if we invalidate get-address-list query, then the index of address is changes when we edit, that's why keep this function here and only remove get-address-list query
            updateAddresses(
              {
                ...res.data.address,
              },
              index ?? -1,
              isEdit
            );
          }
        } catch (e: any) {
          setApiError(e);
        } finally {
          setTransparentLoader(false);
        }
      },
      [
        index,
        setApiError,
        addressValidator,
        closeModal,
        isEdit,
        updateAddresses,
        flashNotification,
        submitAddress,
        queryClient,
      ]
    );

    const setIsPrimaryAndStreetValue = useCallback(() => {
      if (!isEdit || !address) return;
      setIsPrimary(address.is_primary);
      setValue(address.street_name, false);
    }, [isEdit, address, setValue]);

    useEffect(() => {
      setIsPrimaryAndStreetValue();
    }, [setIsPrimaryAndStreetValue]);

    // if there is no any address, make first entered address primary
    useEffect(() => {
      if (addresses?.length === 0) {
        setIsPrimary(true);
        setIsPrimaryCheckboxDisable(true);
      }
    }, [addresses]);

    const clearSuggestionsRef = useOnclickOutside(
      () => {
        clearSuggestions();
      },
      { ignoreClass: ["swal2-container"] }
    );

    const handleSelect = (val: any) => () => {
      setValue(val.description, false);
      clearSuggestions();

      getDetails({ placeId: val.place_id })
        .then((response) => {
          const parsedAddress = parseAddressComponents(
            response.address_components
          );

          setCountry(parsedAddress.country);
          setState(parsedAddress.state);
          setCity(parsedAddress.city);
          setZip_code(parsedAddress.zipCode);
          setStreet_name(parsedAddress.street || val.description);

          setValue(parsedAddress.street || val.description, false);
        })
        .catch((error) => {
          setApiError(error);
        });
    };

    const renderSuggestions = () =>
      data.map((suggestion) => {
        const {
          place_id,
          structured_formatting: { main_text, secondary_text },
        } = suggestion;

        return (
          <li
            className="py-2 first:pt-0 last:pb-0"
            key={place_id}
            onClick={handleSelect(suggestion)}
            role="button"
            aria-label="Suggestions"
          >
            <strong>{main_text}</strong> <strong>{secondary_text}</strong>
          </li>
        );
      });

    const handleInput = (e: any) => {
      setValue(e.target.value);
      setStreet_name(e.target.value);
    };

    // Handle the delete action
    const handleDeleteClick = () => {
      if (
        (value !== address?.street_name ||
          secondaryStreet !== address.street_name2 ||
          country !== address.country ||
          state !== address.state ||
          city !== address.city ||
          zip_code !== address.zip_code) &&
        isEdit
      ) {
        setDeleteModal(true);
      } else {
        closeModal(false);
      }
    };

    useEffect(() => {
      const header = document.getElementById("header");
      const footer = document.getElementById("footer");

      document.body.style.overflow = "hidden";
      document.body.style.width = "100%";

      if (header) {
        header.style.zIndex = "0";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }
      return () => {
        // Enable background scroll
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "auto";
        document.body.style.width = "";
        if (header) {
          header.style.zIndex = "";
        }
        if (footer) {
          footer.style.zIndex = "";
        }
      };
    }, []);

    // for address validation
    useEffect(() => {
      const validateAddress = async () => {
        try {
          const res = await axios.post(ADDRESS_VALIDATION_URL, {
            address: {
              addressLines: [street_name],
              locality: city,
              administrativeArea: state,
              postalCode: zip_code,
              regionCode: countryNameToCode[country.toLowerCase()] || country,
            },
          });
          if (res) {
            const unconfirmedFields: string[] = [];
            const {
              addressComponents,
            }: {
              addressComponents: {
                componentName: {
                  text: string;
                  languageCode: string;
                };
                componentType: string;
                confirmationLevel:
                  | "CONFIRMED"
                  | "UNCONFIRMED_BUT_PLAUSIBLE"
                  | "UNCONFIRMED_AND_UNLIKELY";
              }[];
            } = res.data.result.address;
            addressComponents.forEach((i) => {
              if (i.confirmationLevel.includes("UNCONFIRMED")) {
                if (
                  i.componentType === "sublocality_level_1" ||
                  i.componentType === "route"
                ) {
                  unconfirmedFields.push("Street address");
                } else if (i.componentType === "locality") {
                  unconfirmedFields.push("City");
                } else if (i.componentType === "postal_code") {
                  unconfirmedFields.push("Zipcode");
                } else if (
                  i.componentType === "administrative_area_level_1" ||
                  i.componentType === "administrative_area_level_2"
                ) {
                  unconfirmedFields.push("State");
                } else if (i.componentType === "country") {
                  unconfirmedFields.push("Country");
                }
              }
            });
            if (unconfirmedFields.length) {
              setInvalidFields([...new Set(unconfirmedFields)]); // using Set for remove duplicates from array
            } else {
              setInvalidFields([]);
              setIsAddressValidating(false);
            }
            setIsCountryUnsupportedByGoogle(false);
          }
        } catch (e) {
          if (
            e.response.data.error.code === 400 &&
            e.response.data.error.message.includes("Invalid region code")
          ) {
            setInvalidFields([]);
            setIsCountryUnsupportedByGoogle(true);
            setIsAddressValidating(false);
          }
        } finally {
          setIsAddressValidating(false);
        }
      };

      validationRef.current++;
      let timeout: NodeJS.Timeout | null;
      if (
        city &&
        state &&
        country &&
        street_name &&
        zip_code &&
        (!isEdit || (isEdit && validationRef.current > 1))
      ) {
        timeout = setTimeout(() => {
          setIsAddressValidating(true);
          validateAddress();
        }, 400);
      }

      return () => {
        if (timeout) {
          clearTimeout(timeout);
          setIsAddressValidating(false);
        }
      };
    }, [city, state, country, street_name, zip_code, isEdit]);

    return (
      <>
        <div className="fixed w-full h-full bg-[#00000080] top-0 left-0 grid place-items-center z-[999] max-h-[100vh] overflow-auto modal-content">
          <div
            className="max-w-2xl max-md:max-w-[95%] max-md:mx-auto w-full bg-gradient-to-b from-[#1D1159] to-[#00AEEF] relative rounded-[5px_30px_40px_0px] no-shake-animation"
            ref={ref}
            id={formId}
            onAnimationEnd={() =>
              document.getElementById(formId)?.classList.remove("form-shake")
            }
          >
            <img
              src={modalBorder}
              className="absolute top-0 left-0 w-full h-full z-[0]"
              alt=""
            />
            <div className="relative z-10 p-10 max-md:px-4 max-md:pt-6 max-md:pb-10">
              <div className="relative z-[1]">
                <div className="flex items-center justify-between mb-10 max-md:mb-6 max-sm:mb-4">
                  <h2 className="text-primary text-3xl max-md:text-2xl max-sm:text-xl">
                    {title} Address
                  </h2>
                  <button
                    onClick={() => {
                      handleDeleteClick();
                    }}
                    className="text-primary text-4xl max-md:text-3xl max-sm:text-2xl ml-auto duration-150 hover:scale-95"
                    aria-label="close-icon"
                  >
                    <i className="ri-close-circle-line"></i>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-7 max-sm:gap-2 max-sm:grid-cols-1">
                  <div
                    className="col-span-2 max-sm:col-auto"
                    ref={clearSuggestionsRef}
                  >
                    <label
                      className="text-[#D854BB] text-base mb-2 block f-family-mochiy-pop"
                      htmlFor={id + "street"}
                    >
                      Street Address *
                    </label>
                    <div className="relative">
                      <input
                        id="street"
                        className="relative p-5 text-black w-full bg-transparent f-family-mochiy-pop focus-visible:outline-none"
                        value={value}
                        onChange={handleInput}
                        disabled={!ready}
                        placeholder="Enter street address"
                        maxLength={125}
                      />
                      <img
                        className="absolute top-0 left-0 w-full h-full -z-[1]"
                        src={inputBgMd}
                        alt="input-background"
                      />
                      {status === "OK" && (
                        <ul className="address_suggetions p-3 h-56 rounded-xl border-black border-2 overflow-y-auto absolute top-16 z-10 w-full">
                          {renderSuggestions()}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2 max-sm:col-auto">
                    <label
                      className="text-[#D854BB] text-base mb-2 block f-family-mochiy-pop"
                      htmlFor={id + "street_2"}
                    >
                      Street Address 2
                    </label>
                    <div className="relative">
                      <input
                        id="street_2"
                        className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none f-family-mochiy-pop"
                        value={secondaryStreet}
                        maxLength={125}
                        onChange={(e) => setSecondaryStreet(e.target.value)}
                        placeholder="Enter street address 2"
                      />
                      <img
                        className="absolute top-0 left-0 w-full h-full -z-[1]"
                        src={inputBgMd}
                        alt="input-background"
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <label
                      className="text-[#D854BB] text-base mb-2 block f-family-mochiy-pop"
                      htmlFor={id + "country"}
                    >
                      Country *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Enter country"
                        name="country"
                        value={country}
                        onChange={(e) => {
                          setCountry(
                            e.target.value.replace(/[^a-zA-Z\s]/g, "")
                          );
                        }}
                        className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none f-family-mochiy-pop"
                      />
                      <img
                        className="absolute top-0 left-0 w-full h-full -z-[1]"
                        src={inputBgMd}
                        alt="input-background"
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <label
                      className="text-[#D854BB] text-base mb-2 block f-family-mochiy-pop"
                      htmlFor={id + "state"}
                    >
                      State *
                    </label>
                    <div className="relative">
                      <input
                        id={id + "state"}
                        name="state"
                        placeholder="Enter state"
                        type="text"
                        value={state}
                        onChange={(e) => {
                          setState(e.target.value.replace(/[^a-zA-Z\s]/g, ""));
                        }}
                        className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none f-family-mochiy-pop"
                      />

                      <img
                        className="absolute top-0 left-0 w-full h-full -z-[1]"
                        src={inputBgMd}
                        alt="input-background"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      className="text-[#D854BB] text-base mb-2 block f-family-mochiy-pop"
                      htmlFor={id + "city"}
                    >
                      City *
                    </label>
                    <div className="relative">
                      <input
                        id={id + "city"}
                        name="city"
                        placeholder="Enter city"
                        type="text"
                        value={city}
                        onChange={(e) => {
                          setCity(e.target.value.replace(/[^a-zA-Z\s]/g, ""));
                        }}
                        className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none f-family-mochiy-pop"
                      />
                      <img
                        className="absolute top-0 left-0 w-full h-full -z-[1]"
                        src={inputBgMd}
                        alt="input-background"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      className="text-[#D854BB] text-base mb-2 block f-family-mochiy-pop"
                      htmlFor={id + "zip"}
                    >
                      Zip *
                    </label>
                    <div className="relative">
                      <input
                        id={id + "zip"}
                        value={zip_code}
                        className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none f-family-mochiy-pop"
                        type="text"
                        placeholder="Enter zip"
                        name="zip_code"
                        onChange={(e) =>
                          setZip_code(
                            e.target.value.replace(/[^a-zA-Z0-9]/g, "")
                          )
                        }
                      />
                      <img
                        className="absolute top-0 left-0 w-full h-full -z-[1]"
                        src={inputBgMd}
                        alt="input-background"
                      />
                    </div>
                  </div>
                </div>
                {isEdit && address?.is_primary ? (
                  <div className="mt-3 text-blue">Primary Address</div>
                ) : (
                  <div className="flex items-center gap-3 max-md:gap-2 mt-3">
                    <input
                      className="custom-checkbox disabled:opacity-70 disabled:cursor-not-allowed"
                      type="checkbox"
                      id="isPrimary"
                      checked={isPrimary}
                      onChange={(e) => setIsPrimary(e.target.checked)}
                      disabled={isPrimaryCheckboxDisable}
                    />
                    <label htmlFor="isPrimary" className="text-blue">
                      MAKE THIS MY PRIMARY ADDRESS
                    </label>
                  </div>
                )}
                {!isAddressValidating && invalidFields.length !== 0 && (
                  <p className="text-base text-yellow-300 mt-2">
                    Note: We couldn't verify {invalidFields.join(", ")}. Please
                    double-check for any typos or inaccuracies.
                  </p>
                )}
                {!isAddressValidating && isCountryUnsupportedByGoogle && (
                  <p className="text-base text-yellow-300 mt-2">
                    Note: We couldn't verify this address. Please double-check
                    for any typos or inaccuracies.
                  </p>
                )}
              </div>

              <span className="text-[red]">{getValueOrDefault(error, "")}</span>
              <div className="mt-5 flex items-end justify-end max-sm:justify-center">
                <button
                  disabled={
                    city.length === 0 ||
                    state.length === 0 ||
                    country.length == 0 ||
                    street_name.length === 0 ||
                    zip_code.length === 0 ||
                    transparentLoader ||
                    isAddressValidating
                  }
                  className="z-10 disabled:opacity-50 relative text-xl max-md:text-lg customized-button max-w-[200px] w-full rounded-[5px_20px_11px_20px] flex items-center justify-center disabled:cursor-not-allowed"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (!transparentLoader)
                      await submit(
                        {
                          city,
                          country,
                          state,
                          street_name,
                          zip_code,
                          id: address?.id || "",
                          street_name2: secondaryStreet,
                        },
                        isPrimary
                      );
                  }}
                  aria-label="save-button"
                >
                  {transparentLoader || isAddressValidating ? (
                    <i className="ri-loop-right-line btn-loader"></i>
                  ) : isEdit ? (
                    "Save Changes"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {deleteModal && (
          <DeleteConfirmation
            title="Discard Changes?"
            headTitle=""
            setModal={async (val) => {
              if (val) {
                closeModal(false);
                setDeleteModal(false);
              }
              setDeleteModal(false); // close modal after click on confirm delete
            }}
            ref={deteteRef}
            isPassWay={false}
          />
        )}
      </>
    );
  }
);

export default AddAddress;
